import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProjectsList.css';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';

const PAGE_SIZE = 25;

const ProjectsList = ({ search = '', onEdit, reloadKey = 0, showArchived = false }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const fetchList = async (pageNum, term) => {
    setLoading(true);
    setError('');
    try {
      const body = { page: pageNum, limit: PAGE_SIZE, includeArchived: !!showArchived };
      if (term && term.trim()) body.search = term.trim();
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/list`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to load projects');
      setItems(json.data.items || []);
      setTotal(json.data.total || 0);
    } catch (e) {
      setError(e.message || 'Failed to load');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // refetch on page, search, or reloadKey changes
  useEffect(() => {
    fetchList(page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, reloadKey, showArchived]);

  // reset to page 1 when search or archive toggle changes
  useEffect(() => { setPage(1); }, [search, showArchived]);

  if (loading && items.length === 0) {
    return (
      <div className="projects-list-placeholder">
        <div className="placeholder-content"><i className="fas fa-spinner fa-pulse" /> Loading projects…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="projects-list-placeholder">
        <div className="placeholder-content">
          <i className="fas fa-triangle-exclamation" />
          <h2>Unable to load</h2>
          <p>{error}</p>
          <button className="btn primary" onClick={() => fetchList(page, search)}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {items.length === 0 ? (
        <div className="projects-list-placeholder">
          <div className="placeholder-content">
            <i className="fas fa-folder-open"></i>
            <h2>No projects found</h2>
            <p>Try adjusting your search or create a new project</p>
          </div>
        </div>
      ) : (
        <div className="projects-list-grid">
          {items.map((p) => {
            const hasNoTools = typeof p._toolCount === 'number' && p._toolCount === 0;
            const projIsPublic = p.is_public !== false;
            return (
              <div key={p.project_id} className="project-card">
                <div className="proj-main" onClick={() => navigate(`/p/${p.project_id}`)} style={{ cursor: 'pointer' }}>
                  <div className="proj-icon"><i className="fas fa-folder" /></div>
                  <div className="proj-info">
                    <div className="proj-name">
                      {p.name}
                      {!projIsPublic && (
                        <span
                          style={{
                            marginLeft: '8px',
                            fontSize: '0.75em',
                            padding: '2px 7px',
                            borderRadius: '4px',
                            background: 'rgba(239, 68, 68, 0.15)',
                            color: '#f87171',
                            verticalAlign: 'middle'
                          }}
                        >
                          <i className="fas fa-lock" style={{ marginRight: '3px', fontSize: '0.85em' }} />Private
                        </span>
                      )}
                      {hasNoTools && (
                        <i 
                          className="fas fa-exclamation-circle" 
                          style={{ 
                            marginLeft: '8px', 
                            color: '#f59e0b', 
                            fontSize: '0.9em' 
                          }}
                          title="This project has no tools configured. Add vector search tools to enable AI-powered search in chat."
                        />
                      )}
                      {p.archived_at && (
                        <span
                          style={{
                            marginLeft: '8px',
                            fontSize: '0.75em',
                            padding: '2px 7px',
                            borderRadius: '4px',
                            background: 'rgba(107, 114, 128, 0.2)',
                            color: '#9ca3af',
                            verticalAlign: 'middle',
                          }}
                          title="Archived"
                        >
                          <i className="fas fa-box-archive" style={{ marginRight: '3px', fontSize: '0.85em' }} />
                          Archived
                        </span>
                      )}
                    </div>
                    <div className="proj-desc">{p.description}</div>
                    <div className="proj-meta">
                      Updated {new Date(p.modified_at || p.created_at).toLocaleString()}
                      {p.owner?.email && (
                        <span style={{ marginLeft: '10px', opacity: 0.7 }}>
                          <i className="fas fa-user" style={{ marginRight: '3px', fontSize: '0.85em' }} />
                          {p.owner.email.split('@')[0]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button title="Edit" className="proj-edit" onClick={() => onEdit && onEdit(p)}
                  style={p.is_owner === false ? { display: 'none' } : undefined}
                >
                  <i className="fas fa-pen" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="projects-pagination">
        <button className="btn ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}><i className="fas fa-chevron-left" /> Prev</button>
        <div className="page-status">Page {page} of {totalPages}</div>
        <button className="btn ghost" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next <i className="fas fa-chevron-right" /></button>
      </div>
    </div>
  );
};

export default ProjectsList;
